<template>
	<div class="p-0 min-h-screen bg-gray-100">
		<div class="bg-white py-4 px-6 border-b border-gray-200">
			<div class="mb-2">
				<router-link to="/template" class="text-gray-500 no-underline text-sm inline-flex items-center hover:text-gray-700">
					<i class="fa fa-arrow-left mr-2"></i>
					Back to Templates
				</router-link>
			</div>
			<div class="flex justify-between items-center">
				<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Template</h1>
				<button
					class="bg-violet-600 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer flex items-center hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
					@click="saveTemplate"
					:disabled="isSaving"
				>
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</div>

		<div class="p-6">
			<div class="flex gap-0 mb-6 border-b border-gray-200">
				<button
					class="bg-transparent border-none py-3 px-6 text-sm font-medium cursor-pointer"
					:class="activeTab === 'properties' ? 'border-b-2 border-violet-600 text-violet-600' : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700'"
					@click="activeTab = 'properties'"
				>
					Properties
				</button>
				<button
					class="bg-transparent border-none py-3 px-6 text-sm font-medium cursor-pointer"
					:class="activeTab === 'states' ? 'border-b-2 border-violet-600 text-violet-600' : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700'"
					@click="activeTab = 'states'"
				>
					Template States
				</button>
			</div>

			<div v-if="isLoading" class="py-12 px-6 text-center text-gray-500">Loading template...</div>

			<div v-else-if="activeTab === 'properties'" class="bg-white rounded-lg shadow-sm">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900 m-0">Template Properties</h2>
				</div>
				<div class="p-6">
					<div class="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 mb-6">
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
							<input
								v-model="template.name"
								type="text"
								class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none"
							>
						</div>
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Width</label>
							<input
								v-model.number="template.width"
								type="number"
								class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none"
							>
						</div>
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Height</label>
							<input
								v-model.number="template.height"
								type="number"
								class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none"
							>
						</div>
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Bleed Margin</label>
							<input
								v-model.number="template.bleedmargin"
								type="number"
								class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none"
							>
						</div>
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Dynamic Page Margin</label>
							<input
								v-model.number="template.dynamicPageMargin"
								type="number"
								class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none"
							>
						</div>
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Dynamic Page Padding</label>
							<input
								v-model.number="template.dynamicPagePadding"
								type="number"
								class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none"
							>
						</div>
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Dynamic Photo Count</label>
							<input
								v-model.number="template.dynamicPhotoCount"
								type="number"
								class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none"
							>
						</div>
					</div>
					<div class="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
						<div class="p-3 border border-gray-200 rounded-md">
							<label class="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									v-model="template.transformable"
									class="w-4 h-4"
								>
								<span>Transformable</span>
							</label>
							<span class="text-xs text-gray-500 mt-1 block">Can new objects be freely dropped in this template</span>
						</div>
						<div class="p-3 border border-gray-200 rounded-md">
							<label class="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									v-model="template.dynamic"
									class="w-4 h-4"
								>
								<span>Dynamic</span>
							</label>
						</div>
					</div>
				</div>
			</div>

			<div v-else-if="activeTab === 'states'" class="bg-white rounded-lg shadow-sm">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900 m-0">Template States</h2>
					<button
						class="bg-violet-600 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer flex items-center hover:bg-violet-700"
						@click="showAddStateModal = true"
					>
						<i class="fa fa-plus mr-1.5"></i> Add State
					</button>
				</div>
				<div v-if="isLoadingStates" class="py-12 px-6 text-center text-gray-500">Loading states...</div>
				<div v-else class="overflow-x-auto">
					<table class="w-full border-collapse">
						<thead>
							<tr>
								<th class="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">ID</th>
								<th class="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Type</th>
								<th class="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Priority</th>
								<th class="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="state in templateStates" :key="state.id" class="border-b border-gray-200 hover:bg-gray-50">
								<td class="py-3 px-6 text-sm text-gray-700">{{ state.id }}</td>
								<td class="py-3 px-6 text-sm text-gray-700">{{ state.type }}</td>
								<td class="py-3 px-6 text-sm text-gray-700">{{ state.priority ?? '-' }}</td>
								<td class="py-3 px-6 text-sm text-gray-700 text-right">
									<div class="flex justify-end gap-2">
										<button
											class="bg-transparent border border-gray-300 rounded py-1.5 px-2.5 cursor-pointer text-gray-500 min-w-[32px] min-h-[32px] hover:bg-gray-100"
											@click="editState(state)"
											title="Edit"
										>
											<i class="fa fa-pencil"></i>
										</button>
										<button
											class="bg-transparent border border-red-300 rounded py-1.5 px-2.5 cursor-pointer text-red-600 min-w-[32px] min-h-[32px] hover:bg-red-50"
											@click="confirmDeleteState(state)"
											title="Delete"
										>
											<i class="fa fa-trash"></i>
										</button>
									</div>
								</td>
							</tr>
							<tr v-if="templateStates.length === 0">
								<td colspan="4" class="py-12 px-6 text-center text-gray-500">No template states found</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<div v-if="showAddStateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="showAddStateModal = false">
			<div class="bg-white rounded-lg shadow-xl w-full max-w-md">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h3 class="text-lg font-semibold text-gray-900 m-0">Add Template State</h3>
					<button class="bg-transparent border-none text-2xl text-gray-500 cursor-pointer hover:text-gray-700" @click="showAddStateModal = false">&times;</button>
				</div>
				<div class="p-6">
					<div>
						<label class="block text-sm font-medium text-gray-700 mb-1">Type *</label>
						<select v-model="newStateType" class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none">
							<option value="">-- Select Type --</option>
							<option value="photo">photo</option>
							<option value="text">text</option>
						</select>
					</div>
				</div>
				<div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50">
					<button class="bg-white text-gray-700 border border-gray-300 rounded-md py-2 px-4 text-sm font-medium cursor-pointer hover:bg-gray-50" @click="showAddStateModal = false">Cancel</button>
					<button
						class="bg-violet-600 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
						@click="addState"
						:disabled="isAddingState || !newStateType"
					>
						{{ isAddingState ? 'Adding...' : 'Add State' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { TemplateModel, TemplatePositionStateModel } from '@sosocio/creator/interfaces';
import TemplateService from 'services/TemplateService';
import { httpClient } from 'utils/http';
import Swal from 'sweetalert2';

export default defineComponent({
	name: 'EditTemplatePage',
	setup() {
		const route = useRoute();
		const router = useRouter();
		const templateId = route.params.id as string;

		const template = ref<Partial<TemplateModel>>({});
		const templateStates = ref<TemplatePositionStateModel[]>([]);
		const isLoading = ref(false);
		const isLoadingStates = ref(false);
		const isSaving = ref(false);
		const activeTab = ref('properties');
		const showAddStateModal = ref(false);
		const newStateType = ref('');
		const isAddingState = ref(false);

		const loadTemplate = async () => {
			isLoading.value = true;
			try {
				const data = await TemplateService.get(Number(templateId));
				template.value = data;
			} catch (error: any) {
				console.error('Failed to load template:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load template',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const loadStates = async () => {
			isLoadingStates.value = true;
			try {
				const params = new URLSearchParams({
					limit: '0',
					where: JSON.stringify({ templateid: Number(templateId) }),
				});
				const { data } = await httpClient.get<TemplatePositionStateModel[]>(`/api/templatestate?${params}`);
				templateStates.value = data;
			} catch (error: any) {
				console.error('Failed to load states:', error);
			} finally {
				isLoadingStates.value = false;
			}
		};

		const saveTemplate = async () => {
			isSaving.value = true;
			try {
				await TemplateService.update(Number(templateId), template.value);
				Swal.fire({
					title: 'Saved',
					text: 'Template has been saved',
					icon: 'success',
					timer: 1500,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save template',
					icon: 'error',
				});
			} finally {
				isSaving.value = false;
			}
		};

		const addState = async () => {
			if (!newStateType.value) return;

			isAddingState.value = true;
			try {
				const { data } = await httpClient.post('/api/templatestate', {
					templateid: Number(templateId),
					type: newStateType.value,
				});
				templateStates.value.push(data);
				showAddStateModal.value = false;
				newStateType.value = '';
				Swal.fire({
					title: 'Added',
					text: 'Template state has been added',
					icon: 'success',
					timer: 1500,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to add state',
					icon: 'error',
				});
			} finally {
				isAddingState.value = false;
			}
		};

		const editState = (state: TemplatePositionStateModel) => {
			router.push(`/template/${templateId}/state/${state.id}`);
		};

		const confirmDeleteState = async (state: TemplatePositionStateModel) => {
			const result = await Swal.fire({
				title: 'Delete State',
				text: `Are you sure you want to delete this state?`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc2626',
				confirmButtonText: 'Delete',
			});

			if (result.isConfirmed) {
				try {
					await httpClient.delete(`/api/templatestate/${state.id}`);
					templateStates.value = templateStates.value.filter(s => s.id !== state.id);
					Swal.fire({
						title: 'Deleted',
						text: 'State has been deleted',
						icon: 'success',
						timer: 1500,
					});
				} catch (error: any) {
					Swal.fire({
						title: 'Error',
						text: error.message || 'Failed to delete state',
						icon: 'error',
					});
				}
			}
		};

		onMounted(async () => {
			await Promise.all([loadTemplate(), loadStates()]);
		});

		return {
			template,
			templateStates,
			isLoading,
			isLoadingStates,
			isSaving,
			activeTab,
			showAddStateModal,
			newStateType,
			isAddingState,
			saveTemplate,
			addState,
			editState,
			confirmDeleteState,
		};
	},
});
</script>
